import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageSquarePlus, Trash2, User } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

/**
 * Chronological notes panel used by horse and user profile pages.
 *
 * Props:
 *  - targetType: "horse" | "user"
 *  - targetId: string
 *  - testid prefix
 */
export default function NotesPanel({ targetType, targetId, testid = "notes-panel" }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState("");
  const [content, setContent] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // note object pending delete

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/notes", {
        params: { target_type: targetType, target_id: targetId },
      });
      setNotes(data);
    } catch (e) {
      setError(formatApiError(e));
      setNotes([]);
    }
  };

  useEffect(() => { if (targetId) load(); }, [targetId, targetType]);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setAdding(true);
    setError("");
    try {
      await api.post("/notes", {
        target_type: targetType,
        target_id: targetId,
        content: content.trim(),
      });
      setContent("");
      await load();
    } catch (err) {
      if (err?.response?.status === 403) {
        const msg = err?.response?.data?.detail;
        if (msg && typeof msg === "string") setError(msg);
        else if (targetType === "trip") setError("You must be attending this trip to add notes.");
        else setError("You can only add notes to your own horses.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/notes/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
      setConfirmDel(null);
    }
  };

  const canDelete = (n) =>
    user?.id === n.author_user_id || user?.role === "admin" || user?.role === "super_admin";

  return (
    <div className="se-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="se-label">Notes</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 18 }}>Activity & observations</h3>
        </div>
      </div>

      <form onSubmit={add} className="mb-4" data-testid={`${testid}-form`}>
        <textarea
          className="se-input"
          style={{ height: 90, padding: "10px 14px" }}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a note — behaviour, instructions, observations…"
          data-testid={`${testid}-textarea`}
        />
        {error && (
          <div className="mt-2 text-xs px-2 py-1 rounded" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid={`${testid}-error`}>
            {error}
          </div>
        )}
        <div className="flex justify-end mt-2">
          <button type="submit" className="se-btn" disabled={adding || !content.trim()} data-testid={`${testid}-submit`}>
            <MessageSquarePlus size={14} /> {adding ? "Posting…" : "Add note"}
          </button>
        </div>
      </form>

      <div className="space-y-3" data-testid={`${testid}-list`}>
        {notes === null && (
          <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>
        )}
        {notes !== null && notes.length === 0 && (
          <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid={`${testid}-empty`}>
            No notes yet. Be the first to add one.
          </div>
        )}
        {(notes || []).map((n) => (
          <div
            key={n.id}
            className="p-3 rounded-lg"
            style={{ background: "rgba(212,169,106,0.08)", border: "0.5px solid rgba(212,169,106,0.2)" }}
            data-testid={`${testid}-item-${n.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 text-xs" style={{ color: "#8C9E8A" }}>
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: "#7C5C3A", color: "#F5EFE6" }}
                >
                  <User size={12} />
                </div>
                <span style={{ fontWeight: 600, color: "#3B2F1E" }}>{n.author_name}</span>
                <span>·</span>
                <span>{n.author_role || ""}</span>
                <span>·</span>
                <span>{new Date(n.created_at).toLocaleString()}</span>
              </div>
              {canDelete(n) && (
                <button
                  onClick={() => setConfirmDel(n)}
                  className="se-btn se-btn-ghost"
                  style={{ padding: 4 }}
                  data-testid={`${testid}-delete-${n.id}`}
                  aria-label="Delete note"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <p className="mt-2 text-sm whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={remove}
        title="Are you sure you want to delete this note?"
        description="This action cannot be undone."
        confirmLabel="Yes, delete"
        testid={`${testid}-confirm`}
      />
    </div>
  );
}
